import React, { useEffect, useId, useLayoutEffect, useState } from 'react';

/**
 * TextInput Component with Floating Label Animation
 *
 * A modern text input component that implements floating label functionality
 * based on Figma design specifications. The label floats above the input field
 * when the field is focused or has a value.
 *
 * Key Features:
 * - Floating label animation using Tailwind CSS
 * - Automatic icon variant detection (no manual variant prop needed)
 * - Support for prefix and suffix icons
 * - Multiple sizes (small and medium)
 * - Various states (default, focus, error, disabled)
 * - Responsive design following Figma specifications
 * - Accessible with proper labeling
 *
 * Usage Examples:
 *
 * Basic floating label input:
 * <TextInput label="Email Address" placeholder="Enter your email" />
 *
 * With prefix icon:
 * <TextInput
 *   label="Search"
 *   prefixIcon={<SomeIcon />}
 * />
 *
 * With suffix icon:
 * <TextInput
 *   label="Clear field"
 *   suffixIcon={<ClearIcon />}
 * />
 *
 * With both icons:
 * <TextInput
 *   label="Password"
 *   prefixIcon={<LockIcon />}
 *   suffixIcon={<EyeIcon />}
 *   type="password"
 * />
 *
 * With error state:
 * <TextInput
 *   label="Password"
 *   error="Password is required"
 *   type="password"
 * />
 *
 * Small size:
 * <TextInput
 *   label="Search"
 *   size="small"
 *   prefixIcon={<SomeIcon />}
 * />
 */
export interface TextInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /** Label text that will float above the input */
  label?: string;
  /** Error message to display below the input */
  error?: string;
  /** Helper text to display below the input when no error */
  helperText?: string;
  /** Input size variant */
  size?: 'small' | 'medium';
  /** Icon to display at the start of the input */
  prefixIcon?: React.ReactNode;
  /** Icon to display at the end of the input */
  suffixIcon?: React.ReactNode;
  /** Additional left padding for the input placeholder/content (useful when custom wide prefix content is used) */
  placeholderPaddingLeft?: number; // pixels
  /** Explicit width reservation for custom prefix content (overrides automatic icon offset) */
  prefixReservedWidth?: number; // pixels
  /** Absolute left position override (in pixels) for the label when it is floated */
  floatedLabelLeft?: number; // pixels
  /** Text alignment inside input */
  textAlign?: 'left' | 'center' | 'right' | 'justify' | 'start' | 'end';
}

/**
 * TextInput Component Implementation
 *
 * This component provides a modern floating label text input that follows
 * Material Design principles and Figma specifications. The label smoothly
 * animates to the top when the field is focused or contains a value.
 */
const TextInput = ({
  ref,
  label,
  error,
  helperText,
  size = 'medium',
  prefixIcon,
  suffixIcon,
  className = '',
  textAlign = 'left',
  required,
  disabled,
  readOnly,
  value,
  defaultValue,
  placeholderPaddingLeft,
  prefixReservedWidth,
  floatedLabelLeft,
  onFocus,
  onBlur,
  onChange,
  ...props
}: TextInputProps & { ref?: React.Ref<HTMLInputElement> }) => {
  const [isFocused, setIsFocused] = useState(false);
  // For uncontrolled components, track internal value state
  const [internalHasValue, setInternalHasValue] = useState(
    Boolean(defaultValue),
  );
  const [domValue, setDomValue] = useState('');
  // Track if we've performed the initial value sync before first paint to avoid flicker
  const [initialSyncDone, setInitialSyncDone] = useState(false);
  // Use isomorphic layout effect so SSR won't warn
  const useIsomorphicLayoutEffect =
    typeof window !== 'undefined' ? useLayoutEffect : useEffect;
  const inputId = useId();
  // Internal ref to always capture DOM node even if parent passes a function ref (e.g., React Hook Form)
  const internalInputRef = React.useRef<HTMLInputElement | null>(null);

  // Helper to merge forwarded ref (object or function) with internal ref
  const setCombinedRef = (node: HTMLInputElement | null) => {
    internalInputRef.current = node;
    if (typeof ref === 'function') {
      ref(node);
    } else if (ref && typeof ref === 'object') {
      (ref as React.MutableRefObject<HTMLInputElement | null>).current = node;
    }
  };

  // Function to check if a value should be considered as "having content"
  const hasContent = (val: string | number | readonly string[] | undefined) => {
    return val !== undefined && val !== null && val !== '';
  };

  // Automatically determine if prefix icon exists for label positioning
  const hasPrefix = !!prefixIcon;

  // Determine if label should be floating
  // Check value prop, defaultValue, internal state, and actual DOM value (for React Hook Form)
  const shouldFloat =
    isFocused ||
    hasContent(value) ||
    hasContent(defaultValue) ||
    internalHasValue ||
    hasContent(domValue);

  // Removed previous no-op effect that attempted to watch controlled value; floating state derives directly from props.

  // Effect to check DOM value on mount and when ref changes (for React Hook Form)
  // Using layout effect variant so that if an initial value exists (e.g. RHF defaultValues),
  // the label floats before the first painted frame, eliminating overlap flicker.
  useIsomorphicLayoutEffect(() => {
    if (!internalInputRef.current) return;

    const el = internalInputRef.current;

    const syncDomValue = () => {
      if (!el) return;
      const currentValue = el.value;
      setDomValue((prev) => (prev !== currentValue ? currentValue : prev));
    };

    // Initial sync before first paint
    syncDomValue();
    setInitialSyncDone(true);

    // Observe attribute changes
    const observer = new MutationObserver(syncDomValue);
    observer.observe(el, { attributes: true, attributeFilter: ['value'] });

    // Fallback interval (programmatic .value updates without attribute mutation)
    const interval = setInterval(syncDomValue, 200);

    return () => {
      observer.disconnect();
      clearInterval(interval);
    };
  }, []);

  // Handle focus events
  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(true);
    onFocus?.(e);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);
    // Update DOM value tracking
    setDomValue(e.target.value);
    // Only update internal state for uncontrolled components
    if (value === undefined) {
      setInternalHasValue(hasContent(e.target.value));
    }
    onBlur?.(e);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Update DOM value tracking for React Hook Form compatibility
    setDomValue(e.target.value);
    // Only update internal state for uncontrolled components
    if (value === undefined) {
      setInternalHasValue(hasContent(e.target.value));
    }
    onChange?.(e);
  };

  // Size-specific styles
  const sizeStyles = {
    small: {
      container: 'h-10',
      padding: 'px-4 py-2',
      iconSize: 'size-4',
      fontSize: 'text-sm leading-5',
      labelFloated: 'text-xs leading-4 -top-2',
      labelNormal: 'text-sm leading-5',
      iconOffset: '28px',
      labelOffset: '16px',
    },
    medium: {
      container: 'h-12',
      padding: 'px-4 py-3',
      iconSize: 'size-5',
      fontSize: 'leading-6',
      labelFloated: 'text-xs leading-4 -top-2',
      labelNormal: 'leading-6',
      iconOffset: '36px',
      labelOffset: '16px',
    },
  };

  const currentSize = sizeStyles[size];
  const isReadOnly = Boolean(readOnly) && !disabled;

  // State-specific styles
  const getContainerStyles = () => {
    const baseStyles = `bg-[white] box-border content-stretch flex flex-row gap-2 items-center justify-start relative rounded-lg shrink-0 w-full border transition-all duration-200 ${currentSize.container} ${currentSize.padding}`;

    if (disabled) {
      return `${baseStyles} border-border-disabled bg-bg-disabled cursor-not-allowed`;
    } else if (isReadOnly) {
      return `${baseStyles} border-border-default bg-[white] cursor-default`;
    } else if (error) {
      return `${baseStyles} border-error focus-within:border-error focus-within:ring-1 focus-within:ring-error`;
    } else if (isFocused) {
      return `${baseStyles} border-primary ring-0.5 ring-primary`;
    } else {
      return `${baseStyles} border-border-default hover:border-border-hover focus-within:border-primary focus-within:ring-1 focus-within:ring-primary`;
    }
  };

  // Icon and text color styles
  const getIconStyles = () => {
    if (disabled) return 'text-border-disabled';
    if (isReadOnly) return 'text-secondary-text';
    if (error) return 'text-error';
    if (isFocused) return 'text-primary';
    return 'text-secondary-text';
  };

  const getTextStyles = () => {
    if (disabled) return 'text-border-disabled';
    if (isReadOnly) return 'text-secondary-text';
    return 'text-primary-text';
  };

  const getLabelStyles = () => {
    if (error && shouldFloat) return 'text-error';
    if (isFocused && shouldFloat && !isReadOnly) return 'text-label';
    if (shouldFloat) return 'text-label';
    if (disabled) return 'text-border-disabled';
    return 'text-secondary-text';
  };

  // Calculate label position based on whether prefix icon exists
  const getLabelPosition = () => {
    if (hasPrefix) {
      if (prefixReservedWidth !== undefined) {
        return `${prefixReservedWidth}px`; // small gap inside
      }
      return currentSize.iconOffset;
    }
    return currentSize.labelOffset;
  };

  // Use explicit prop to control text alignment (default: left)
  const textAlignClass = `text-${textAlign}`;

  return (
    <div className="relative flex w-full flex-col items-start gap-1 p-0">
      {/* Input Field Container */}
      <div className={getContainerStyles()}>
        {/* Prefix Icon / Custom Prefix Region */}
        {prefixIcon &&
          (prefixReservedWidth !== undefined ? (
            <div className={`flex items-center p-0 ${getIconStyles()}`}>
              {prefixIcon}
            </div>
          ) : (
            <div
              className={`flex items-center justify-center p-0 ${currentSize.iconSize} ${getIconStyles()}`}
            >
              {prefixIcon}
            </div>
          ))}

        {/* Input Field */}
        <div className="relative flex grow flex-col p-0">
          <input
            ref={setCombinedRef}
            id={inputId}
            className={`relative shrink-0 font-normal not-italic ${getTextStyles()} ${currentSize.fontSize} w-full border-none bg-transparent ${textAlignClass} outline-none ${label ? 'placeholder-transparent' : 'placeholder-text-disabled'} ${className}`}
            disabled={disabled}
            readOnly={readOnly}
            required={required}
            value={value}
            defaultValue={defaultValue}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onChange={handleChange}
            aria-describedby={
              error
                ? `${inputId}-error`
                : helperText
                  ? `${inputId}-helper`
                  : undefined
            }
            style={
              placeholderPaddingLeft
                ? { paddingLeft: placeholderPaddingLeft }
                : undefined
            }
            {...props}
          />
        </div>

        {/* Suffix Icon */}
        {suffixIcon && (
          <div
            className={`flex items-center justify-center p-0 ${currentSize.iconSize} ${getIconStyles()}`}
          >
            {suffixIcon}
          </div>
        )}

        {/* Floating Label */}
        {label && (
          <div
            className={`absolute z-10 px-1 text-left font-normal whitespace-nowrap ${initialSyncDone ? 'transition-all duration-200 ease-in-out' : 'transition-none'} pointer-events-none ${getLabelStyles()} ${
              shouldFloat
                ? `${currentSize.labelFloated} transform`
                : `${currentSize.labelNormal} top-1/2 -translate-y-1/2 transform`
            } `}
            style={{
              left:
                shouldFloat && typeof floatedLabelLeft === 'number'
                  ? `${floatedLabelLeft}px`
                  : getLabelPosition(),
            }}
          >
            {/* Background mask for floating label */}
            {shouldFloat && (
              <div className="absolute top-1/2 right-0 left-0 -z-10 h-1 -translate-y-1/2 transform bg-[white]" />
            )}
            <label htmlFor={inputId} className="relative z-10">
              {label}
              {required && <span className="text-error">*</span>}
            </label>
          </div>
        )}
      </div>

      {/* Helper Text or Error Message */}
      {(error || helperText) && (
        <div className="flex w-full items-start gap-2 pl-4">
          <div
            id={error ? `${inputId}-error` : `${inputId}-helper`}
            className={`text-left text-sm leading-5 font-normal ${
              error ? 'text-error' : 'text-text-disabled'
            }`}
          >
            {error || helperText}
          </div>
        </div>
      )}
    </div>
  );
};

TextInput.displayName = 'TextInput';

export default TextInput;
